import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isDetailsOpen: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isDetailsOpen: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isDetailsOpen: false,
    });
  };

  toggleDetails = () => {
    this.setState((prevState) => ({
      isDetailsOpen: !prevState.isDetailsOpen,
    }));
  };

  goToDashboard = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#f9fafb',
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", sans-serif',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              padding: '40px',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
            }}
          >
            {/* Sad Robot SVG */}
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              style={{
                margin: '0 auto 24px',
                display: 'block',
                
              }}
            >
              {/* Robot Head */}
              <rect x="20" y="15" width="40" height="35" fill="none" stroke="#6b7280" strokeWidth="2" rx="4" />

              {/* Eyes */}
              <circle cx="30" cy="28" r="4" fill="#6b7280" />
              <circle cx="50" cy="28" r="4" fill="#6b7280" />

              {/* Sad Mouth */}
              <path
                d="M 30 38 Q 40 35 50 38"
                stroke="#6b7280"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
              />

              {/* Antenna */}
              <line x1="40" y1="15" x2="40" y2="5" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
              <circle cx="40" cy="3" r="2" fill="#6b7280" />

              {/* Body */}
              <rect x="22" y="52" width="36" height="20" fill="none" stroke="#6b7280" strokeWidth="2" rx="2" />

              {/* Buttons on body */}
              <circle cx="30" cy="58" r="2" fill="#6b7280" />
              <circle cx="40" cy="58" r="2" fill="#6b7280" />
              <circle cx="50" cy="58" r="2" fill="#6b7280" />
            </svg>

            {/* Heading */}
            <h1
              style={{
                margin: '0 0 12px 0',
                fontSize: '24px',
                fontWeight: '600',
                color: '#1f2937',
              }}
            >
              Something went wrong
            </h1>

            {/* Description */}
            <p
              style={{
                margin: '0 0 24px 0',
                fontSize: '14px',
                color: '#6b7280',
                lineHeight: '1.5',
              }}
            >
              We encountered an unexpected error. Our team has been notified. Please try again or return to the dashboard.
            </p>

            {/* Error Details Section */}
            {this.state.error && (
              <div
                style={{
                  marginBottom: '24px',
                  textAlign: 'left',
                }}
              >
                <button
                  onClick={this.toggleDetails}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '0',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s ease',
                      transform: this.state.isDetailsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  >
                    ▶
                  </span>
                  Error Details
                </button>

                {this.state.isDetailsOpen && (
                  <div
                    style={{
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}
                  >
                    <p
                      style={{
                        margin: '0 0 8px 0',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#374151',
                      }}
                    >
                      Error Message:
                    </p>
                    <pre
                      style={{
                        margin: '0 0 12px 0',
                        fontSize: '11px',
                        color: '#6b7280',
                        backgroundColor: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #e5e7eb',
                        overflow: 'auto',
                        fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", monospace',
                      }}
                    >
                      {this.state.error.toString()}
                    </pre>

                    {this.state.errorInfo && (
                      <>
                        <p
                          style={{
                            margin: '0 0 8px 0',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#374151',
                          }}
                        >
                          Stack Trace:
                        </p>
                        <pre
                          style={{
                            margin: '0',
                            fontSize: '11px',
                            color: '#6b7280',
                            backgroundColor: 'white',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #e5e7eb',
                            overflow: 'auto',
                            fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", monospace',
                          }}
                        >
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
              }}
            >
              <button
                onClick={this.resetError}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
              >
                Try Again
              </button>

              <button
                onClick={this.goToDashboard}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#e5e7eb';
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
